import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');

  // Track if verification is in progress to prevent duplicate calls
  const verificationInProgress = useRef(false);

  const brandGreen = '#9be38f';
  const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    // Don't run if already verifying or already completed
    if (verificationInProgress.current || status !== 'verifying') return;

    const verifyEmail = async () => {
      verificationInProgress.current = true;

      try {
        console.log('Calling verification API:', `${API_URL}/api/auth/verify/${token}`);

        const response = await fetch(`${API_URL}/api/auth/verify/${token}`);

        console.log('Response status:', response.status);

        const data = await response.json();

        console.log('Response data:', data);

        if (data.success) {
          setStatus('success');
          setMessage(data.message);

          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/early-access?verified=true');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed');
          setErrorCode(data.errorCode || data.code || '');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification. Please try again.');
      } finally {
        verificationInProgress.current = false;
      }
    };

    // Only verify if token exists
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('No verification token provided.');
    }
  }, [token, navigate, API_URL, status]);

  return (
    <div className="min-h-screen w-full bg-background grid-pattern" data-testid="verify-email-page">
      <ThemeToggle className="fixed top-4 right-4 z-[60]" />
      {/* Top-left brand */}
      <div className="px-8 pt-7">
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-black/90 dark:text-white"
          style={{ fontFamily: "'Lora', Georgia, serif" }}
        >
          <img src={pluckrLogo} alt="Pluckr" className="h-9 w-auto" />
          <span className="text-[22px] font-bold tracking-[-0.01em]">Pluckr</span>
        </Link>
      </div>

      <div className="max-w-[1200px] mx-auto px-8 pb-16 pt-10">
        <div className="flex flex-col items-center justify-center">
          {/* Verification Card */}
          <div className="w-full max-w-[500px]">
            <div
              className="bg-white dark:bg-[#282929] rounded-[18px] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-black/5 dark:border-white/15 p-10 text-center"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >

              {/* Verifying State */}
              {status === 'verifying' && (
                <>
                  <div
                    className="w-16 h-16 border-4 border-gray-200 dark:border-white/20 rounded-full mx-auto mb-8"
                    style={{
                      borderTopColor: brandGreen,
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <h2 className="text-[28px] font-bold text-black/80 dark:text-white mb-3">
                    Verifying Your Email...
                  </h2>
                  <p className="text-[16px] text-black/60 dark:text-white/70">
                    Please wait while we confirm your email address.
                  </p>
                </>
              )}

              {/* Success State */}
              {status === 'success' && (
                <>
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8"
                    style={{
                      backgroundColor: brandGreen,
                      animation: 'scaleIn 0.3s ease-out'
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1a1a1a"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h2
                    className="text-[28px] font-bold mb-4"
                    style={{ color: '#22c55e' }}
                  >
                    Email Verified Successfully! ✅
                  </h2>
                  <p className="text-[16px] text-black/60 dark:text-white/70 mb-6 leading-relaxed">
                    {message || 'Your email has been verified. You can now log in to Pluckr.'}
                  </p>
                  <p className="text-[14px] text-black/40 dark:text-white/50 mb-8">
                    Redirecting to login page in 3 seconds...
                  </p>
                  <button
                    onClick={() => navigate('/early-access?verified=true')}
                    className="w-full h-12 rounded-full font-semibold text-[14px] text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: '#1f2d2a' }}
                  >
                    Continue to Login
                  </button>
                </>
              )}

              {/* Error State */}
              {status === 'error' && (
                <>
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8"
                    style={{
                      backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                      animation: 'scaleIn 0.3s ease-out'
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </div>
                  <h2
                    className="text-[28px] font-bold mb-4"
                    style={{ color: '#ef4444' }}
                  >
                    Verification Failed ❌
                  </h2>
                  <p className="text-[16px] text-black/60 dark:text-white/70 mb-8 leading-relaxed">
                    {message || "We couldn't verify your email. The link may be invalid or expired."}
                  </p>

                  <div className="space-y-3">
                    {errorCode === 'TOKEN_EXPIRED' && (
                      <Link
                        to="/resend-verification"
                        className="block w-full h-12 rounded-full font-semibold text-[14px] text-white transition-all hover:opacity-90 flex items-center justify-center"
                        style={{ backgroundColor: '#1f2d2a' }}
                      >
                        Resend Verification Email
                      </Link>
                    )}

                    <Link
                      to="/signup"
                      className="block w-full h-12 rounded-full font-semibold text-[14px] text-black/70 dark:text-white/80 transition-all hover:bg-black/5 dark:hover:bg-white/5 flex items-center justify-center border border-black/10 dark:border-white/20"
                    >
                      Back to Sign Up
                    </Link>

                    <Link
                      to="/early-access"
                      className="block text-[13px] text-black/50 dark:text-white/60 hover:text-black/70 dark:hover:text-white pt-2"
                    >
                      Already have an account? Log in →
                    </Link>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
